import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { BlockedProductListEntry } from 'contracts/blocked-products';
import type { CreateServiceOrderPayload } from 'contracts/service-orders';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { InputGs, TextareaGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components/activityIndicators';
import { ProductsTable } from 'pages/stock-inspection/shared-components';

import { TasksSelector, type TasksSelectorRef } from './tasks-selector';
import { formSchema, type FormSchema } from './form-schema';

export type CreateServiceOrderDrawerRef = {
  open: (products: BlockedProductListEntry[]) => void;
  close: () => void;
}

type Props = {
  onSubmit: (data: CreateServiceOrderPayload) => void;
  loading?: boolean;
}

export const CreateServiceOrderDrawer =
  React.forwardRef<CreateServiceOrderDrawerRef, Props>(({
                                                          onSubmit,
                                                          loading,
                                                        }, ref) => {
    const [products, setProducts] = React.useState<BlockedProductListEntry[]>([]);
    const [noTaskSelected, setNoTaskSelected] = React.useState(false);
    const [open, setOpen] = React.useState(false);

    const tasksSelectorRef = React.useRef<TasksSelectorRef>(null);

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
    });

    const onFormSubmit = React.useCallback((formData: FormSchema) => {
      const tasks = tasksSelectorRef.current?.getData() ?? [];

      if (!tasks.length) {
        return setNoTaskSelected(true);
      }

      onSubmit({
        productIds: products.map(p => p.id),
        invoiceNumber: formData?.invoiceNumber,
        observation: formData?.observation ?? null,
        tasks,
      });
    }, [products, onSubmit]);

    const onOpenChange = React.useCallback((open: boolean) => {
      setOpen(open);
      form.reset();
      setNoTaskSelected(false);
      tasksSelectorRef.current?.reset();
    }, [form]);

    React.useImperativeHandle(ref, () => ({
      open: (selected) => {
        onOpenChange(selected.length > 0);
        setProducts(selected);
      },
      close: () => {
        onOpenChange(false);
        setProducts([]);
      },
    }), [onOpenChange]);

    return (
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Content>
          <div className="max-w-[800px] w-full mx-auto max-h-screen overflow-y-auto">
            <Drawer.Header>
              <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
                <span>Produtos selecionados</span>
                <span className={'font-extrabold text-gray-800 text-[40px]'}>
                {products.length.toString().padStart(3, '0')}
              </span>
              </Drawer.Title>
            </Drawer.Header>
            <ProductsTable products={products} className={'max-h-[260px] overflow-y-auto mb-6'} />
            <div className={'mb-6'}>
              <TasksSelector ref={tasksSelectorRef} />
              {noTaskSelected && (
                <div className={'text-sm text-destructive font-GilroySemibold mt-4'}>
                  Selecione ao menos um trabalho
                </div>
              )}
            </div>
            <Form {...form} >
              <form
                onSubmit={form.handleSubmit(onFormSubmit)}
                className="flex flex-col gap-4 pb-10 px-1"
              >
                <div className={'grid grid-cols-2 gap-4'}>
                  <InputGs
                    readOnly
                    disabled
                    className={'bg-gray-100'}
                    value={'10 d'}
                    label={'Tempo estimado'}
                  />
                  <FormField
                    control={form.control}
                    name={'invoiceNumber'}
                    render={({ field }) => <InputGs {...field} label={'Número da NF'} />}
                  />
                </div>
                <FormField
                  control={form.control}
                  name={'observation'}
                  render={({ field }) => (
                    <TextareaGs
                      {...field}
                      label={'Observação'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <div className={'grid grid-cols-2 gap-4'}>
                  <Button.Root
                    type={'button'}
                    size={'sm'}
                    variant={'secondary'}
                    onClick={() => onOpenChange(false)}
                  >
                    Cancelar
                  </Button.Root>
                  <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                    {loading ? <ActivityIndicator /> : 'Salvar'}
                  </Button.Root>
                </div>
              </form>
            </Form>
          </div>
        </Drawer.Content>
      </Drawer.Root>
    );
  });
CreateServiceOrderDrawer.displayName = 'CreateServiceOrderDrawer';